import { Card, EmptyState, SkeletonText } from '@aurora/ui-kit';
import { useQuery } from '@tanstack/react-query';
import { BarChart3 } from 'lucide-react';

import { api } from '../api/client.js';

type MetricFormat = 'money' | 'number' | 'percent' | 'hours';

interface ReportMetric {
  label: string;
  value: number;
  format: MetricFormat;
  hint?: string;
}
interface ReportBar {
  label: string;
  value: number;
  color?: string;
}
interface ReportChart {
  title: string;
  format: MetricFormat;
  bars: ReportBar[];
}
interface ReportSection {
  key: string;
  title: string;
  metrics: ReportMetric[];
  charts?: ReportChart[];
}

/** Akzent-Farbnamen der StatusPill-Palette → CSS-Werte (light+dark tauglich). */
const BAR_COLORS: Record<string, string> = {
  gray: 'var(--au-text-faint)',
  blue: '#3b82f6',
  green: '#16a34a',
  yellow: '#d4a72c',
  orange: '#ea7317',
  red: '#dc2626',
  purple: '#9333ea',
  teal: '#0d9488',
  pink: '#db2777',
};

function formatValue(value: number, format: MetricFormat): string {
  switch (format) {
    case 'money':
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(
        value / 100,
      );
    case 'percent':
      return `${Math.round(value)} %`;
    case 'hours':
      return `${value.toLocaleString('de-DE', { maximumFractionDigits: 1 })} h`;
    case 'number':
    default:
      return value.toLocaleString('de-DE');
  }
}

/** Waagerechtes Balkendiagramm — reines SVG/CSS, keine Fremdbibliothek. */
function BarChart({ chart }: { chart: ReportChart }) {
  const max = Math.max(1, ...chart.bars.map((bar) => bar.value));
  const hasData = chart.bars.some((bar) => bar.value > 0);

  return (
    <div className="report-chart">
      <div className="report-chart__title">{chart.title}</div>
      {!hasData ? (
        <p className="report-chart__empty">Noch keine Daten.</p>
      ) : (
        <ul className="report-bars">
          {chart.bars.map((bar, index) => (
            <li key={index} className="report-bar">
              <span className="report-bar__label" title={bar.label}>
                {bar.label}
              </span>
              <span className="report-bar__track">
                <span
                  className="report-bar__fill"
                  style={{
                    width: `${Math.max(2, (bar.value / max) * 100)}%`,
                    background: bar.color
                      ? (BAR_COLORS[bar.color] ?? 'var(--au-accent)')
                      : 'var(--au-accent)',
                  }}
                />
              </span>
              <span className="report-bar__value">{formatValue(bar.value, chart.format)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * v4: Berichte — modulübergreifendes Auswertungs-Dashboard. Die Abschnitte
 * liefern die aktivierten Module über die REPORTS-Registry; der Host
 * aggregiert sie. Kein Modul kennt das Dashboard, es wächst automatisch mit.
 */
export default function ReportsPage() {
  const reports = useQuery({
    queryKey: ['reports'],
    queryFn: () => api<{ sections: ReportSection[] }>('/api/v1/reports'),
  });

  const today = new Date().toLocaleDateString('de-DE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const sections = reports.data?.sections ?? [];

  return (
    <div className="page page--wide">
      <div>
        <h1 className="page__title">Berichte</h1>
        <p className="page__lead">Auswertungen über alle Module — Stand {today}.</p>
      </div>

      {reports.isLoading ? (
        <SkeletonText lines={8} />
      ) : sections.length === 0 ? (
        <Card>
          <EmptyState
            icon={<BarChart3 size={28} aria-hidden />}
            title="Noch keine Auswertungen"
            description="Sobald Fachmodule mit Daten aktiv sind, erscheinen hier Kennzahlen und Diagramme."
          />
        </Card>
      ) : (
        <div className="report-sections">
          {sections.map((section) => (
            <Card key={section.key} title={section.title}>
              <div className="report-metrics">
                {section.metrics.map((metric, index) => (
                  <div key={index} className="report-metric">
                    <div className="report-metric__value">
                      {formatValue(metric.value, metric.format)}
                    </div>
                    <div className="report-metric__label">{metric.label}</div>
                    {metric.hint !== undefined ? (
                      <div className="report-metric__hint">{metric.hint}</div>
                    ) : null}
                  </div>
                ))}
              </div>
              {(section.charts ?? []).map((chart, index) => (
                <BarChart key={index} chart={chart} />
              ))}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
